import React from 'react';
import rrService from '../services/rrService';

const OrderFormDialog = ({
  isOpen,
  onClose,
  trades,
  issues,
  warnings,
  formatCurrency,
  selectedGroup,
  accountMapping,
  accountCustodianMapping
}) => {
  if (!isOpen) return null;
  const hasCriticalIssues = issues.length > 0;
  // Handle order generation
  const handleGenerateOrders = async () => {
    try {
      // Group trades by account for processing
      const tradesByAccount = trades.reduce((acc, trade) => {
        const account = trade.account;
        if (!acc[account]) {
          // Use the reliable account mapping to get account number and custodian
          const accountNumber = accountMapping?.[account] || trade.accountNumber;
          const custodian = accountCustodianMapping?.[accountNumber] || trade.custodian || 'Unknown';

          acc[account] = {
            accountNumber: accountNumber,
            custodian: custodian,
            trades: []
          };
        }
        acc[account].trades.push(trade);
        return acc;
      }, {});

      let successCount = 0;
      let errorCount = 0;

      // Generate order forms for each account
      for (const [accountName, accountData] of Object.entries(tradesByAccount)) {
        // Double check custodian filtering
        const validCustodians = ['fidelity', 'charles schwab'];
        const isValidCustodian = validCustodians.some(valid =>
          accountData.custodian.toLowerCase().includes(valid)
        );

        if (!isValidCustodian) {
          console.warn(`Skipping account ${accountName} with unsupported custodian: ${accountData.custodian}`);
          continue;
        }

        try {
          const response = await rrService.generateOrderForm(
            selectedGroup,
            accountName,
            accountData.accountNumber,
            accountData.custodian,
            accountData.trades
          );

          // Validate response before processing
          if (!response || !response.data) {
            throw new Error('Invalid response from server');
          }
          // Check if response.data is a Blob or Blob-like object
          const blob = response.data;
          const isBlobLike = blob &&
            typeof blob.size === 'number' &&
            typeof blob.type === 'string' &&
            (blob instanceof Blob || typeof blob.arrayBuffer === 'function');

          if (!isBlobLike) {
            throw new Error('Response is not a valid file blob');
          }

          // Trigger download
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.style.display = 'none';
          a.href = url;
          a.download = `${accountData.custodian.replace(/\s+/g, '_')}_Order_${accountData.accountNumber}_${new Date().toISOString().slice(0, 10)}.txt`;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
          successCount++;
        } catch (accountError) {
          console.error('Failed to generate order form for account:', accountName, accountError);
          errorCount++;
        }
      }

      // Show user feedback
      if (successCount > 0) {
        alert(`Successfully generated ${successCount} order form${successCount > 1 ? 's' : ''}!`);
      }
      if (errorCount > 0) {
        alert(`Failed to generate ${errorCount} order form${errorCount > 1 ? 's' : ''}. Check console for details.`);
      }

      // Close dialog after processing
      onClose();
    } catch (error) {
      console.error('Error generating order forms:', error);
      alert('Error generating order forms. Check console for details.');
    }
  };

  return (
    <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-xl modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <i className="bi bi-file-earmark-text me-2"></i>
              Order Form Summary
            </h5>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
            ></button>
          </div>

          <div className="modal-body">
            {/* Summary Header */}
            <div className="row mb-4">
              <div className="col-md-4">
                <div className="card text-center">
                  <div className="card-body">
                    <h5 className="card-title text-primary">{trades.length}</h5>
                    <p className="card-text">Total Trades</p>
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="card text-center">
                  <div className="card-body">
                    <h5 className="card-title text-danger">{issues.length}</h5>
                    <p className="card-text">Critical Issues</p>
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="card text-center">
                  <div className="card-body">
                    <h5 className="card-title text-warning">{warnings.length}</h5>
                    <p className="card-text">Warnings</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Issues and Warnings */}
            {(issues.length > 0 || warnings.length > 0) && (
              <div className="mb-4">
                <h6>Issues and Warnings</h6>

                {issues.map((issue, index) => (
                  <div key={`issue-${index}`} className="alert alert-danger mb-2">
                    <div className="d-flex justify-content-between align-items-start">
                      <div>
                        <strong>
                          <i className="bi bi-exclamation-triangle-fill me-2"></i>
                          Critical Issue:
                        </strong>
                        <div className="mt-1">
                          <strong>{issue.trade.symbol}</strong> in <strong>{issue.trade.account}</strong>
                          <br />
                          {issue.message}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                {warnings.map((warning, index) => (
                  <div key={`warning-${index}`} className={`alert ${warning.severity === 'info' ? 'alert-info' : 'alert-warning'} mb-2`}>
                    <div className="d-flex justify-content-between align-items-start">
                      <div>
                        <strong>
                          <i className={`bi ${warning.severity === 'info' ? 'bi-info-circle' : 'bi-exclamation-triangle'} me-2`}></i>
                          {warning.severity === 'info' ? 'Information:' : 'Warning:'}
                        </strong>
                        <div className="mt-1">
                          <strong>{warning.trade.symbol}</strong> in <strong>{warning.trade.account}</strong>
                          <br />
                          {warning.message}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}            {/* All Trades Table */}
            <div className="mb-4">
              <h6>All Trades (Grouped by Account)</h6>
              {trades.length === 0 ? (
                <div className="alert alert-info">
                  <i className="bi bi-info-circle me-2"></i>
                  No trades have been configured yet.
                </div>
              ) : (
                (() => {
                  // Group trades by account
                  const tradesByAccount = trades.reduce((acc, trade, index) => {
                    const account = trade.account;
                    if (!acc[account]) {
                      acc[account] = [];
                    }
                    acc[account].push({ ...trade, originalIndex: index });
                    return acc;
                  }, {});

                  return (
                    <div className="accordion" id="tradesAccordion">
                      {Object.entries(tradesByAccount).map(([account, accountTrades], accountIndex) => {
                        const accountIssues = accountTrades.filter(trade =>
                          issues.find(issue => issue.tradeIndex === trade.originalIndex)
                        );
                        const accountWarnings = accountTrades.filter(trade =>
                          warnings.find(warning => warning.tradeIndex === trade.originalIndex)
                        );

                        const hasAccountIssues = accountIssues.length > 0;
                        const hasAccountWarnings = accountWarnings.length > 0;

                        const cardClass = hasAccountIssues ? 'border-danger' :
                          hasAccountWarnings ? 'border-warning' : 'border-success';
                        const headerClass = hasAccountIssues ? 'bg-danger text-white' :
                          hasAccountWarnings ? 'bg-warning text-dark' : 'bg-success text-white';

                        return (
                          <div key={account} className={`card mb-2 ${cardClass}`}>
                            <div className={`card-header ${headerClass}`} id={`heading${accountIndex}`}>
                              <h6 className="mb-0">
                                <button
                                  className={`btn btn-link ${hasAccountIssues ? 'text-white' : hasAccountWarnings ? 'text-dark' : 'text-white'} text-decoration-none d-flex justify-content-between align-items-center w-100`}
                                  type="button"
                                  data-bs-toggle="collapse"
                                  data-bs-target={`#collapse${accountIndex}`}
                                  aria-expanded="true"
                                  aria-controls={`collapse${accountIndex}`}
                                >
                                  <div className="d-flex align-items-center">
                                    <i className="bi bi-building me-2"></i>
                                    <strong>{account}</strong>
                                    {/* Show custodian */}
                                    {accountTrades.length > 0 && accountTrades[0].custodian && (
                                      <span className="ms-2 text-muted small">({accountTrades[0].custodian})</span>
                                    )}
                                    <span className="ms-2 badge bg-light text-dark">
                                      {accountTrades.length} trade{accountTrades.length !== 1 ? 's' : ''}
                                    </span>
                                  </div>
                                  <div className="d-flex align-items-center">
                                    {hasAccountIssues && (
                                      <span className="badge bg-light text-danger me-1">
                                        <i className="bi bi-exclamation-triangle-fill"></i> {accountIssues.length} Issue{accountIssues.length !== 1 ? 's' : ''}
                                      </span>
                                    )}
                                    {hasAccountWarnings && (
                                      <span className="badge bg-light text-warning me-1">
                                        <i className="bi bi-exclamation-triangle"></i> {accountWarnings.length} Warning{accountWarnings.length !== 1 ? 's' : ''}
                                      </span>
                                    )}
                                    {!hasAccountIssues && !hasAccountWarnings && (
                                      <span className="badge bg-light text-success me-1">
                                        <i className="bi bi-check-circle"></i> All OK
                                      </span>
                                    )}
                                    <i className="bi bi-chevron-down"></i>
                                  </div>
                                </button>
                              </h6>
                            </div>
                            <div
                              id={`collapse${accountIndex}`}
                              className="collapse show"
                              aria-labelledby={`heading${accountIndex}`}
                              data-bs-parent="#tradesAccordion"
                            >
                              <div className="card-body p-0">
                                <div className="table-responsive">
                                  <table className="table table-sm mb-0">
                                    <thead className="table-light">
                                      <tr>
                                        <th>Symbol</th>
                                        <th>Security Name</th>
                                        <th>Action</th>
                                        <th>Quantity</th>
                                        <th>Sleeve</th>
                                        <th>Status</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {accountTrades.map((trade) => {
                                        const hasIssue = issues.find(issue => issue.tradeIndex === trade.originalIndex);
                                        const hasWarning = warnings.find(warning => warning.tradeIndex === trade.originalIndex);
                                        const rowClass = hasIssue ? 'table-danger' : hasWarning ? 'table-warning' : '';

                                        return (
                                          <tr key={trade.originalIndex} className={rowClass}>
                                            <td><strong>{trade.symbol}</strong></td>
                                            <td>{trade.securityName}</td>
                                            <td>
                                              <span className={`badge ${trade.action === 'Buy' ? 'bg-success' : 'bg-danger'}`}>
                                                {trade.action}
                                              </span>
                                            </td>
                                            <td>{trade.quantity.toLocaleString()}</td>
                                            <td className="text-muted small">{trade.sleeveName}</td>
                                            <td>
                                              {hasIssue && (
                                                <span className="badge bg-danger me-1" title={hasIssue.message}>
                                                  <i className="bi bi-exclamation-triangle-fill"></i> Issue
                                                </span>
                                              )}
                                              {hasWarning && (
                                                <span className={`badge ${hasWarning.severity === 'info' ? 'bg-info' : 'bg-warning'} me-1`} title={hasWarning.message}>
                                                  <i className={`bi ${hasWarning.severity === 'info' ? 'bi-info-circle' : 'bi-exclamation-triangle'}`}></i>
                                                  {hasWarning.severity === 'info' ? ' Info' : ' Warning'}
                                                </span>
                                              )}
                                              {!hasIssue && !hasWarning && (
                                                <span className="badge bg-success">
                                                  <i className="bi bi-check-circle"></i> OK
                                                </span>
                                              )}
                                            </td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()
              )}
            </div>
          </div>

          <div className="modal-footer">
            <div className="d-flex justify-content-between w-100">
              <div className="text-muted small">
                {hasCriticalIssues ?
                  'Please resolve critical issues before proceeding.' :
                  'All trades appear to be valid.'
                }
              </div>
              <div>
                <button
                  type="button"
                  className="btn btn-secondary me-2"
                  onClick={onClose}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={handleGenerateOrders}
                  disabled={hasCriticalIssues}
                >
                  <i className="bi bi-download me-2"></i>
                  Generate & Download Order Forms
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderFormDialog;
